import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, GitBranchPlus, Atom, Leaf, Tags, FlaskConical, Loader2, Coins, CheckCircle2 } from "lucide-react";
import { SOP_RAFT_TEMPLATE, getSubstitutes, costEstimate } from "@/lib/atlasApi";

const TYPE_META = {
  Ingredient: { color: "#00FF9D", icon: Leaf, label: "Insumo" },
  ChemicalCompound: { color: "#00E5FF", icon: Atom, label: "Composto Químico" },
  FlavorCategory: { color: "#F59E0B", icon: Tags, label: "Categoria de Sabor" },
};

function Row({ k, v }) {
  if (v === undefined || v === null || v === "") return null;
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-[#1F2937]">
      <span className="text-xs uppercase tracking-wider text-slate-500">{k}</span>
      <span className="text-sm text-slate-200 text-right break-all">{String(v)}</span>
    </div>
  );
}

export default function InspectorPanel({ node, onExpand, canExport = false }) {
  const [subs, setSubs] = useState(null);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [cost, setCost] = useState(null);

  useEffect(() => {
    setSubs(null);
    setCost(null);
    if (node?.ntype === "Ingredient" && node?.sop_raft_id) {
      costEstimate([{ sop_raft_id: node.sop_raft_id, quantity: 100 }])
        .then(setCost)
        .catch(() => setCost(null));
    }
  }, [node?.sop_raft_id, node?.ntype]);

  if (!node) {
    return (
      <div className="p-6 text-sm text-slate-400" data-testid="inspector-empty">
        Clique num nó do grafo para inspecionar. Duplo-clique expande a vizinhança.
      </div>
    );
  }
  const meta = TYPE_META[node.ntype] || { color: "#94A3B8", icon: Atom, label: node.ntype };
  const Icon = meta.icon;
  const isIng = node.ntype === "Ingredient";
  const link = isIng && node.sop_raft_id ? SOP_RAFT_TEMPLATE.replace("{sop_raft_id}", encodeURIComponent(node.sop_raft_id)) : null;

  const loadSubs = async () => {
    setLoadingSubs(true);
    try {
      const res = await getSubstitutes(node.sop_raft_id, 5, 0.05);
      setSubs(res.substitutes || []);
    } catch (e) {
      setSubs([]);
    } finally {
      setLoadingSubs(false);
    }
  };

  return (
    <div className="p-5 space-y-4" data-testid="inspector-panel">
      <div className="flex items-center gap-3">
        <div className="rounded-lg p-2" style={{ backgroundColor: `${meta.color}22` }}>
          <Icon className="h-5 w-5" style={{ color: meta.color }} />
        </div>
        <div>
          <div className="text-lg font-semibold text-white" data-testid="inspector-node-name">{node.label}</div>
          <Badge variant="outline" className="mt-0.5 border-[#1F2937] text-slate-300">{meta.label}</Badge>
        </div>
      </div>

      <div className="rounded-lg bg-[#0B0F19] border border-[#1F2937] p-3">
        <Row k="sop_raft_id" v={node.sop_raft_id} />
        <Row k="categoria" v={node.category} />
        <Row k="perfil" v={node.flavor_profile} />
        <Row k="CAS" v={node.cas_number} />
        <Row k="aroma" v={node.aroma_descriptor} />
      </div>

      {isIng && (node.molecular_explanation || (node.harmonization_categories && node.harmonization_categories.length > 0)) && (
        <div className="rounded-lg bg-[#0B0F19] border border-[#00E5FF]/25 p-3 space-y-2" data-testid="inspector-enrichment">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#00E5FF]">
            <FlaskConical className="h-3.5 w-3.5" /> Análise molecular (IA)
          </div>
          {node.molecular_explanation && (
            <p className="text-xs text-slate-300 leading-relaxed">{node.molecular_explanation}</p>
          )}
          {node.synergy_notes && (
            <p className="text-xs text-slate-400 leading-relaxed">
              <span className="text-slate-500">Sinergia: </span>{node.synergy_notes}
            </p>
          )}
          {node.thermodynamic_notes && (
            <p className="text-xs text-slate-400 leading-relaxed">
              <span className="text-slate-500">Termodinâmica: </span>{node.thermodynamic_notes}
            </p>
          )}
          {Array.isArray(node.harmonization_categories) && node.harmonization_categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {node.harmonization_categories.map((c, i) => (
                <Badge key={i} variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF] text-[10px]">{c}</Badge>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {node.sop_raft_id && (
          <Button
            data-testid="inspector-expand-btn"
            variant="outline"
            className="w-full border-[#1F2937] hover:border-[#00FF9D]"
            onClick={() => onExpand && onExpand(node.sop_raft_id)}
          >
            <GitBranchPlus className="h-4 w-4 mr-2 text-[#00FF9D]" /> Expandir vizinhança
          </Button>
        )}

        {isIng && node.sop_raft_id && (
          <Button
            data-testid="inspector-substitutes-btn"
            variant="outline"
            className="w-full border-[#1F2937] hover:border-[#FF6B2B]"
            onClick={loadSubs}
            disabled={loadingSubs}
          >
            {loadingSubs ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2 text-[#FF6B2B]" />}
            Substitutos químicos
          </Button>
        )}

        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer" data-testid="inspector-sopraft-link">
            <Button className="w-full bg-[#00E5FF] text-black hover:bg-[#06B6D4]">
              <ExternalLink className="h-4 w-4 mr-2" /> Ficha técnica no SOP &amp; RAFT
            </Button>
          </a>
        )}

        {isIng && node.sop_raft_id && cost && (
          <div className="rounded-lg bg-[#0B0F19] border border-[#1F2937] p-3" data-testid="inspector-cost">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">
                <Coins className="h-3.5 w-3.5 text-[#FACC15]" /> Custo estimado (demo · 100g)
              </span>
              <span className="text-sm font-semibold text-[#FACC15]">{cost.currency} {cost.total_cost}</span>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">{cost.note}</p>
          </div>
        )}

        {isIng && node.sop_raft_id && (
          <p className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400/90" data-testid="inspector-autosync-note">
            <CheckCircle2 className="h-3.5 w-3.5" /> Harmonizações sincronizadas automaticamente ao ecossistema
          </p>
        )}
      </div>

      {subs !== null && (
        <div className="rounded-lg bg-[#0B0F19] border border-[#1F2937] p-3 space-y-2" data-testid="inspector-substitutes">
          <div className="text-xs uppercase tracking-wider text-slate-500">Substitutos por sobreposição molecular</div>
          {subs.length === 0 && <p className="text-sm text-slate-400">Nenhum substituto relevante encontrado.</p>}
          {subs.map((s) => (
            <button
              key={s.sop_raft_id}
              data-testid={`substitute-${s.sop_raft_id}`}
              onClick={() => onExpand && onExpand(s.sop_raft_id)}
              className="w-full text-left rounded-md border border-[#1F2937] hover:border-[#FF6B2B]/60 bg-[#111827] p-2.5 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-100">{s.name}</span>
                <Badge variant="outline" className="border-[#FF6B2B]/40 text-[#FF6B2B]">
                  {Math.round((s.similarity || 0) * 100)}%
                </Badge>
              </div>
              <p className="text-xs text-slate-400 mt-1">{s.rationale}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
